import { IMsalContext } from "@azure/msal-react";
import { algoApiRequest } from "../../authConfig";
import { Algo, AlgoConfiguration } from "./types";
import { AlgoSpecName } from "../configTab/algoSpec";
import {
  COMPANY,
  getLazerHubBaseUri,
} from "../../MFTEnvironmentVariables";
import dayjs from "dayjs";
import {
  EXCHANGE,
  Exchange,
} from "../../Services/ExchangeSelectorContextProvider";
import { ProductType } from "../../Services/nats/contracts";

const request_id_header = "x-request-id";

let lazerhub_uri: string | undefined = undefined;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
(window as any).setLazerHubBaseUri = (value: any) => {
  lazerhub_uri = value;
};

export const ltUrl = (): string => {
  return lazerhub_uri || getLazerHubBaseUri();
};

/**
 * A lazerhub URL scoped to this deployment's company. lazerhub is shared with
 * Skynet, and defaults to the European desk when the parameter is absent — so
 * omitting it on the fan-out routes (stop-all, bulk stop) would act on
 * Europe's trading instances instead of ours.
 *
 * No endpoint here has a query string of its own, hence the plain `?`.
 */
const ltUrlWithCompany = (endpoint: string): string =>
  `${ltUrl()}${endpoint}?company=${COMPANY}`;

const acquireToken = async (msal: IMsalContext) => {
  const { instance, accounts } = msal;
  const accessTokenRequest = {
    ...algoApiRequest,
    account: instance.getActiveAccount() || accounts[0],
  };

  try {
    const response = await instance.acquireTokenSilent(accessTokenRequest);
    const accessToken = response.accessToken;
    return accessToken;
  } catch (e) {
    console.error(
      "AcquireToken(msal) - Silent token acquisition failed. Redirecting to login. ",
      e
    );
    instance.acquireTokenRedirect(accessTokenRequest);
    throw e;
  }
};

const getHeaders = (accessToken?: string) => {
  const headers: HeadersInit = {
    "Content-Type": "application/json",
    Accept: "application/json",
  };
  if (accessToken) {
    headers["Authorization"] = "Bearer " + accessToken;
  }
  return headers;
};

/**
 * A hub response body, parsed as JSON.
 *
 * The hub's error convention is a JSON body carrying `reason`, but not every
 * failure follows it — some come back as a plain-text body. `response.json()`
 * would replace that text with a bare `SyntaxError: Unexpected token …`, so the
 * trader saw the parse failure instead of the hub's reason. The body is read as
 * text first, and one that is not JSON is thrown verbatim instead.
 */
const readJson = async (
  response: Response,
  action: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Promise<any> => {
  const text = await response.text();
  try {
    return JSON.parse(text);
  } catch {
    const request_id = response.headers.get(request_id_header);
    throw new Error(
      `${action} failed (HTTP ${response.status}): ` +
      (text || response.statusText || "empty response") +
      " with request_id: " +
      request_id
    );
  }
};

export type CreateAlgoRequest = {
  algo_id: string;
  algo_spec: {
    /**
     * An {@link AlgoSpecName} for the algos this UI creates, but not only
     * those: a bulk margin update echoes each algo's own spec back, which may
     * be a `_shimurai` variant this form never builds from scratch.
     */
    name: AlgoSpecName | string;
    version: string | undefined;
  };
  product_definition: {
    delivery_start: dayjs.Dayjs;
    delivery_end: dayjs.Dayjs;
    delivery_area_id: string;
    product_types: ProductType[];
  };
  start?: string;
  end?: string;
  configuration: AlgoConfiguration;
};

export const create_algo = async (
  exchange: Exchange | undefined,
  msal: IMsalContext,
  request: CreateAlgoRequest
): Promise<string> => {
  let createAlgoEndpoint = "/v1/algos";
  if (exchange !== undefined) {
    createAlgoEndpoint = `/v1/${exchange}/algos`;
  }
  const accessToken = await acquireToken(msal);

  return await fetch(ltUrlWithCompany(createAlgoEndpoint), {
    method: "POST",
    headers: getHeaders(accessToken),
    body: JSON.stringify(request),
  }).then((response: Response) =>
    readJson(response, "Algo creation").then((json) => {
      const reason = json.reason;
      if (reason !== undefined) {
        const request_id = response.headers.get(request_id_header);
        throw new Error(
          "Algo creation failed: " +
          reason.message +
          " with request_id: " +
          request_id
        );
      }
      return "Algo created: " + json.algo_id;
    })
  );
};

export const update_algo = async (
  exchange: Exchange | undefined,
  msal: IMsalContext,
  request: CreateAlgoRequest
): Promise<string> => {
  let updateAlgoEndpoint = "/v1/algos";
  if (exchange !== undefined) {
    updateAlgoEndpoint = `/v1/${exchange}/algos`;
  }

  const accessToken = await acquireToken(msal);

  return await fetch(ltUrlWithCompany(updateAlgoEndpoint), {
    method: "PUT",
    headers: getHeaders(accessToken),
    body: JSON.stringify(request),
  }).then((response: Response) =>
    readJson(response, "Algo update").then((json) => {
      const reason = json.reason;
      if (reason !== undefined) {
        const request_id = response.headers.get(request_id_header);
        throw new Error(
          "Algo update failed: " +
          reason.message +
          " with request_id: " +
          request_id
        );
      }
      return "Algo updated: " + json.algo_id;
    })
  );
};

/**
 * Update several algos in one call — the same endpoint and verb as
 * {@link update_algo}, with a JSON **array** as the body.
 *
 * Used by the shimurai margin override, where the desk retunes every selected
 * algo at once. It is one request, so it is all-or-nothing as far as this
 * client can tell: there is no per-algo result to report, only the hub's
 * single answer.
 */
export const update_algos = async (
  exchange: Exchange | undefined,
  msal: IMsalContext,
  requests: CreateAlgoRequest[]
): Promise<string> => {
  let updateAlgoEndpoint = "/v1/algos";
  if (exchange !== undefined) {
    updateAlgoEndpoint = `/v1/${exchange}/algos`;
  }

  const accessToken = await acquireToken(msal);

  return await fetch(ltUrlWithCompany(updateAlgoEndpoint), {
    method: "PUT",
    headers: getHeaders(accessToken),
    body: JSON.stringify(requests),
  }).then((response: Response) =>
    readJson(response, "Algo update").then((json) => {
      // A list body may come back as a list; `reason` can sit on the envelope
      // or on any one entry, so both shapes are checked before declaring
      // success.
      const entries: unknown[] = Array.isArray(json) ? json : [json];
      const failed = entries.find(
        (entry) =>
          entry !== null &&
          typeof entry === "object" &&
          (entry as { reason?: unknown }).reason !== undefined
      ) as { reason?: { message?: string } } | undefined;

      if (failed !== undefined) {
        const request_id = response.headers.get(request_id_header);
        throw new Error(
          "Algo update failed: " +
          failed.reason?.message +
          " with request_id: " +
          request_id
        );
      }
      return `${requests.length} algo${requests.length === 1 ? "" : "s"} updated`;
    })
  );
};

export type Uptime = {
  exchange: string;
  last_updated_at?: Date;
  started_at?: Date;
  // Set by the emergency/normal mode toggle below. The hub omits the key while
  // it does not know the mode yet, so absent is not the same as "Normal".
  execution_mode?: "Normal" | "Emergency";
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const parseUptime = (uptimes: any[]): Uptime[] => {
  return uptimes.map((uptime) => ({
    ...uptime,
    last_updated_at: uptime.last_updated_at
      ? new Date(uptime.last_updated_at)
      : undefined,
    started_at: uptime.started_at ? new Date(uptime.started_at) : undefined,
  }));
};

export type ListAlgosResponse = {
  algos: Algo<AlgoConfiguration>[];
  uptimes: Uptime[];
  meta: {
    lazerTraderStartTime: Date;
  };
};

export type ExecutionModeEndpoint = "emergency_mode" | "normal_mode";

/**
 * Puts JEPX into, or takes it out of, emergency mode. lazerhub sets or clears
 * `EMERGENCYMODE` on the JEPX evergreen container, which stops (or restarts)
 * every algo on the exchange. The resulting mode is read back from
 * `uptimes[].execution_mode` on the polled `v1/algos` response.
 *
 * Same flow as Skynet, which fans this out over several exchanges; the desk
 * only has one. The route is exchange-scoped, so `?company=` is ignored by the
 * hub and only kept for consistency with the rest of this file.
 */
export const setExecutionMode = async (
  msal: IMsalContext,
  endpoint: ExecutionModeEndpoint,
  description: string
): Promise<void> => {
  const accessToken = await acquireToken(msal);

  const response = await fetch(
    ltUrlWithCompany(`/v1/${EXCHANGE}/operator/${endpoint}`),
    {
      method: "POST",
      headers: getHeaders(accessToken),
    }
  );

  if (!response.ok) {
    const request_id = response.headers.get(request_id_header);
    throw new Error(
      `${description} failed for ${EXCHANGE.toUpperCase()}, with request-id: ${request_id}`
    );
  }
};

/**
 * The JEPX container's `startwithoutexchange` variable, through lazerhub's
 * operator routes. On means the connector comes up talking to nobody: no socket
 * to JEPX, no market data, every request answered locally.
 *
 * It is container configuration, not a runtime switch, so it only bites when the
 * container next starts. Nothing reads the variable back, so both directions are
 * blind writes: the caller cannot know, or show, which one is in force.
 *
 * The route is exchange-scoped, so lazerhub takes the company from the `jepx`
 * path segment and needs an MFTAPAC3 operator role; `?company=` is along for
 * consistency with the rest of this file and is ignored here.
 */
export const setStartWithoutExchange = async (
  msal: IMsalContext,
  withoutExchange: boolean
): Promise<void> => {
  const endpoint = withoutExchange
    ? "/v1/jepx/operator/start_without_exchange"
    : "/v1/jepx/operator/with_exchange";
  const accessToken = await acquireToken(msal);

  const response = await fetch(ltUrlWithCompany(endpoint), {
    method: "POST",
    headers: getHeaders(accessToken),
  });

  if (!response.ok) {
    const request_id = response.headers.get(request_id_header);
    throw new Error(
      `Failed to ${
        withoutExchange ? "set" : "clear"
      } startwithoutexchange, with request-id: ${request_id}`
    );
  }
};

export const stopAll = async (msal: IMsalContext): Promise<void> => {
  const stop_all_endpoint = "/v1/algos/all";
  const accessToken = await acquireToken(msal);
  await fetch(ltUrlWithCompany(stop_all_endpoint), {
    method: "DELETE",
    headers: getHeaders(accessToken),
  }).then((response) => {
    if (response.status > 399) {
      const request_id = response.headers.get(request_id_header);
      throw new Error(
        "Failed to stop all algos, with request-id: " + request_id
      );
    }
  });
};

export const stopAlgoBulk = async (
  msal: IMsalContext,
  algo_ids: string[]
): Promise<void> => {
  const stop_algo_endpoint = "/v1/algos";
  const accessToken = await acquireToken(msal);
  return await fetch(ltUrlWithCompany(stop_algo_endpoint), {
    method: "DELETE",
    headers: getHeaders(accessToken),
    body: JSON.stringify(algo_ids),
  }).then((response) =>
    readJson(response, "Stopping algos").then((json) => {
      const message = json.message;
      if (message !== undefined) {
        const request_id = response.headers.get(request_id_header);
        const message_with_id = message + ", with request_id: " + request_id;
        throw message_with_id;
      }
    })
  );
};
